import React from 'react'
import eqxApiInstance from '../classes/eqxApi'
import eqxEndpoint from '../classes/eqxEndpoint'

/** Turn thrown/caught API errors (strings, Error-like objects, JSON bodies with `message` or `error`) into a single line for UI. */
export function apiErrorToString(err: unknown): string {
    if (err == null) return 'Unknown error'
    if (typeof err === 'string') return err
    if (typeof err === 'object') {
        const o = err as Record<string, unknown>
        if (typeof o.message === 'string' && o.message) return o.message
        if (typeof o.error === 'string' && o.error) return o.error
        if (Object.keys(o).length === 0) return 'Request failed'
        try {
            return JSON.stringify(o)
        } catch {
            return 'Request failed'
        }
    }
    return String(err)
}

/**
 *
 */
function useApiEndpoint<ResponseType, InputType = null>(
    api: eqxApiInstance,
    endpoint?: eqxEndpoint<ResponseType, InputType> | string,
    allowRequest: boolean = true,
    data?: InputType,
    cachedValue?: ResponseType | null
): {
    response: ResponseType | null
    loading: boolean
    error: string | null
    status: 'not-requested' | 'loading' | 'error' | 'success'
    set: (response: ResponseType | null) => void
    reset: (quietly?: boolean) => void
} {
    if (typeof endpoint == 'string') endpoint = new eqxEndpoint<ResponseType, InputType>(endpoint)
    const [requested, setRequested] = React.useState<boolean>(false)
    const [error, setError] = React.useState<string | null>(null)
    const [response, setResponse] = React.useState<any>(cachedValue ?? null)
    const [loading, setLoading] = React.useState<boolean>(cachedValue ? false : true)
    const [wasReset, setWasReset] = React.useState<boolean>(false)

    // If cachedValue is provided and we haven't reset, return it immediately
    React.useEffect(() => {
        if (cachedValue && !requested && !wasReset) {
            setResponse(cachedValue)
            setLoading(false)
            setError(null)
            setRequested(true)
        }
    }, [cachedValue, requested, wasReset])

    //because it's a hook, hard to not call it if endpoint is optional. This allows us to proceed with a blank endpoint, so that later code can stop the component
    if (!endpoint)
        return {
            response: null,
            loading: false,
            error: null,
            status: 'not-requested',
            set: () => { },
            reset: (quietly: boolean = false) => { },
        }

    if (!requested && allowRequest && (!cachedValue || wasReset)) {
        setRequested(true)
        endpoint
            .execute(api, data)
            .then((theResponse) => {
                setResponse(theResponse as ResponseType)
                setLoading(false)
            })
            .catch((error) => {
                setError(apiErrorToString(error))
                setLoading(false)
            })
    }
    return {
        response,
        loading,
        error,
        status: !requested ? 'not-requested' : loading ? 'loading' : error ? 'error' : 'success',
        set: setResponse,
        reset: (quietly: boolean = false) => {
            setRequested(false)
            setError(null)
            setWasReset(true)
            if (!quietly) setResponse(null)
            setLoading(true)
        },
    }
}

export default useApiEndpoint
